import React, { useContext, useState } from "react";
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Collapse,
  Paper,
  Grid,
  useTheme,
  Snackbar,
  Alert,
} from "@mui/material";
import {
  Email as EmailIcon,
  Phone as PhoneIcon,
  SmartToy as AIIcon,
  Notifications as NotificationsIcon,
  Chat as ChatIcon,
  Reply as ReplyIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Settings as SettingsIcon,
} from "@mui/icons-material";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import { UserContext } from "../../context/UserProvider";
import LeadsNotificationToggle from "./LeadsNotificationToggle";
import LocationMessage from "./LocationMessage";
import EmailSettings from "./EmailSettings";
import PhoneSettings from "./PhoneSettings";
import AiToggleSettings from "./AiToggleSettings";

const WhatsAppSettings = () => {
  const theme = useTheme();
  const { user: userData } = useContext(UserContext);
  const [activeMenu, setActiveMenu] = useState("email");
  const [expandedMenus, setExpandedMenus] = useState(["ai"]);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const menus = [
    { id: "email", label: "Email Notification", icon: EmailIcon },
    { id: "phone", label: "Phone Notification", icon: PhoneIcon },
    {
      id: "locationMsg",
      label: "Location Message",
      icon: LocationOnOutlinedIcon,
    },

    {
      id: "ai",
      label: "AI Subscriptions",
      icon: AIIcon,
      submenus: [
        { id: "leads", label: "Leads Notification", icon: NotificationsIcon },
        { id: "talk", label: "Talk to Leads", icon: ChatIcon },
        { id: "auto", label: "Auto Reply", icon: ReplyIcon },
      ],
    },
  ];

  const handleMenuToggle = (menuId) => {
    setExpandedMenus((prev) =>
      prev.includes(menuId)
        ? prev.filter((id) => id !== menuId)
        : [...prev, menuId]
    );
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const renderContent = () => {
    switch (activeMenu) {
      case "email":
        return <EmailSettings userData={userData} setSnackbar={setSnackbar} />;
      case "phone":
        return <PhoneSettings userData={userData} setSnackbar={setSnackbar} />;
      case "locationMsg":
        return <LocationMessage />;
      case "leads":
        return <LeadsNotificationToggle />;
      case "talk":
      case "auto":
        return <AiToggleSettings activeMenu={activeMenu} />;
      default:
        return (
          <Box textAlign="center" py={4}>
            <SettingsIcon sx={{ fontSize: 48, color: "text.secondary" }} />
            <Typography color="text.secondary">
              Select a setting to configure
            </Typography>
          </Box>
        );
    }
  };

  return (
    <>
      <Paper variant="outlined" sx={{ borderRadius: 3, overflow: "hidden" }}>
        <Grid container>
          {/* Sidebar */}
          <Grid
            item
            xs={12}
            md={5}
            sx={{ borderRight: 1, borderColor: "divider" }}
          >
            <Box sx={{ p: 2 }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                WhatsApp Settings
              </Typography>
              <List dense>
                {menus.map((menu) => (
                  <Box key={menu.id}>
                    <ListItem disablePadding>
                      <ListItemButton
                        onClick={() =>
                          menu.submenus
                            ? handleMenuToggle(menu.id)
                            : setActiveMenu(menu.id)
                        }
                        selected={
                          activeMenu === menu.id ||
                          menu.submenus?.some((sub) => sub.id === activeMenu)
                        }
                        sx={{
                          borderRadius: 1,
                          "&.Mui-selected": {
                            bgcolor: theme.palette.action.selected,
                          },
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <menu.icon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary={menu.label} />
                        {menu.submenus &&
                          (expandedMenus.includes(menu.id) ? (
                            <ExpandLessIcon fontSize="small" />
                          ) : (
                            <ExpandMoreIcon fontSize="small" />
                          ))}
                      </ListItemButton>
                    </ListItem>

                    {menu.submenus && (
                      <Collapse in={expandedMenus.includes(menu.id)}>
                        <List dense disablePadding sx={{ pl: 3 }}>
                          {menu.submenus.map((sub) => (
                            <ListItem key={sub.id} disablePadding>
                              <ListItemButton
                                onClick={() => setActiveMenu(sub.id)}
                                selected={activeMenu === sub.id}
                                sx={{
                                  borderRadius: 1,
                                  "&.Mui-selected": {
                                    bgcolor: theme.palette.action.hover,
                                  },
                                }}
                              >
                                <ListItemIcon sx={{ minWidth: 28 }}>
                                  <sub.icon fontSize="small" />
                                </ListItemIcon>
                                <ListItemText
                                  primary={sub.label}
                                  primaryTypographyProps={{
                                    fontSize: "0.85rem",
                                  }}
                                />
                              </ListItemButton>
                            </ListItem>
                          ))}
                        </List>
                      </Collapse>
                    )}
                  </Box>
                ))}
              </List>
            </Box>
          </Grid>

          {/* Main Action Workspace */}
          <Grid item xs={12} md={7}>
            <Box sx={{ p: 3 }}>{renderContent()}</Box>
          </Grid>
        </Grid>
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default WhatsAppSettings;
